import type { DynamicEntityRow } from '../api/strapiDynamicEntity';

const SURVEY_READY_STATUSES = new Set(['draft', 'submitted', 'approved']);

const CHECKLIST_MANY_FIELDS = [
  'tujuan_pengambilan',
  'pengawetan',
  'larutan_penjerap',
  'peralatan_pengambilan_contoh',
  'peralatan_pengukuran_lapangan',
  'peralatan_pendukung',
  'peralatan_k3',
  'cara_pengambilan_contoh',
  'pengendalian_mutu_lapangan',
  'pengamanan_transportasi_contoh',
  'administrasi_contoh',
  'metode_sampling',
  'sni_methods',
] as const;

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function docId(row: Record<string, unknown> | null | undefined): string {
  return String(row?.documentId ?? '').trim();
}

function relDocId(rel: unknown): string {
  return docId(asRecord(rel));
}

function relationMany(row: DynamicEntityRow | null | undefined, field: string): unknown[] {
  const raw = row?.[field];
  return Array.isArray(raw) ? raw : [];
}

export function filterSurveyReadyPlans(plans: DynamicEntityRow[]): DynamicEntityRow[] {
  return [...plans]
    .filter((plan) => {
      const airGroup = String(plan.air_group ?? '').trim();
      if (airGroup === 'air' || airGroup === 'non_air') return false;
      return SURVEY_READY_STATUSES.has(String(plan.progress_status ?? '').trim());
    })
    .sort((a, b) => Number(a.line_index ?? 0) - Number(b.line_index ?? 0));
}

export function planGroupKey(plan: DynamicEntityRow): string {
  const lineDocId = relDocId(plan.quotation_sample_information);
  if (lineDocId) return `line:${lineDocId}`;
  const idx = Number(plan.line_index);
  if (Number.isFinite(idx)) return `idx:${idx}`;
  return `plan:${docId(plan)}`;
}

/** Skor kelengkapan checklist admin WO pada sampling plan. */
export function samplingPlanCompletenessScore(plan: DynamicEntityRow): number {
  let score = 0;
  if (relDocId(plan.bidang_pengujian)) score += 20;
  for (const field of CHECKLIST_MANY_FIELDS) {
    if (relationMany(plan, field).length > 0) score += 4;
  }
  if (String(plan.jumlah_wadah_contoh_uji_air ?? '').trim()) score += 2;
  if (String(plan.jumlah_wadah_contoh_uji_padatan ?? '').trim()) score += 2;
  return score;
}

function comparePlanCandidates(
  a: DynamicEntityRow,
  b: DynamicEntityRow,
  letterCountByPlanId: Map<string, number>,
): number {
  const baA = letterCountByPlanId.get(docId(a)) ?? 0;
  const baB = letterCountByPlanId.get(docId(b)) ?? 0;
  if (baB !== baA) return baB - baA;

  const scoreA = samplingPlanCompletenessScore(a);
  const scoreB = samplingPlanCompletenessScore(b);
  if (scoreB !== scoreA) return scoreB - scoreA;

  const ta = Date.parse(String(a.updatedAt ?? '')) || 0;
  const tb = Date.parse(String(b.updatedAt ?? '')) || 0;
  return tb - ta;
}

/** Satu plan kanonik per baris sampel: utamakan plan yang sudah punya berita acara, lalu checklist terlengkap. */
export function pickCanonicalSamplingPlans(
  plans: DynamicEntityRow[],
  letterCountByPlanId: Map<string, number> = new Map(),
): DynamicEntityRow[] {
  const ready = filterSurveyReadyPlans(plans);
  const groups = new Map<string, DynamicEntityRow[]>();
  for (const plan of ready) {
    const key = planGroupKey(plan);
    const list = groups.get(key) ?? [];
    list.push(plan);
    groups.set(key, list);
  }

  const picked: DynamicEntityRow[] = [];
  for (const candidates of groups.values()) {
    const best = [...candidates].sort((a, b) => comparePlanCandidates(a, b, letterCountByPlanId))[0];
    if (best) picked.push(best);
  }

  return picked.sort((a, b) => Number(a.line_index ?? 0) - Number(b.line_index ?? 0));
}

export function letterCountBySamplingPlanFromLetters(
  letters: ReadonlyArray<Record<string, unknown>>,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const letter of letters) {
    const planId = relDocId(letter.sampling_plan);
    if (!planId) continue;
    counts.set(planId, (counts.get(planId) ?? 0) + 1);
  }
  return counts;
}

export function letterCountBySamplingPlanFromRows(
  rows: Array<{ sampling_plan_document_id?: string; berita_acara_document_id?: string | null }>,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (!row.berita_acara_document_id) continue;
    const planId = String(row.sampling_plan_document_id ?? '').trim();
    if (!planId) continue;
    counts.set(planId, (counts.get(planId) ?? 0) + 1);
  }
  return counts;
}
